import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { ChevronDownIcon, PhoneIcon } from 'lucide-react';
interface FAQPageProps {
  onNavigate: (page: string) => void;
}
const faqs = [{
  question: 'Do I Pay For Delivery?',
  answer: "No. We don't charge for delivery or pickups in an effort to pass along any savings to you. We also don't charge redelivery or pick-up costs if we miss you."
}, {
  question: 'What Types Of Mobility Products Do You Rent?',
  answer: 'Currently, we rent transporter wheelchairs, power chairs, scooters, and manual wheelchairs. To reach a representative, call us at +1 202-367-9965 or +1 800-395-4459 toll-free.'
}, {
  question: 'What Is Recommended Time Frame To Book A Rental?',
  answer: 'We advise giving yourself at least 72 hours to reserve your rental. To guarantee equipment availability, you must make your rental reservations a week in advance.'
}, {
  question: 'Can I Reach A Staff If My Wheelchair Or Scooter Breaks Down?',
  answer: 'Yes, you will be given a phone number to contact if your scooter or wheelchair breaks down during delivery. However, we do not expect any issues with the scooter or wheelchair because our rental fleet is routinely cleaned and examined for flaws before delivering it to the next client after each rental.'
}, {
  question: 'What Are Your Scooter & Wheelchair Rental Rates?',
  answer: "Since we frequently offer promotions, it is nearly impossible to display our rates. However, we promise to offer the best pricing in the Washington, D.C., region. We don't charge you for delivery, pickup, or redelivery as other businesses do. Call 1-800-395-4459 or 1-202-367-9965 to talk with a representative."
}, {
  question: 'How Do I Pay For My Rental?',
  answer: 'Major credit cards, including Visa, MasterCard, AMEX, and others, are accepted as forms of payment.'
}, {
  question: 'Can I Take My Mobility Scooter Outside The State I Rented From?',
  answer: 'Yes. When you rent equipment from us, you have it until the end of your rental period. So you can take it on a trip with you.'
}, {
  question: 'Do I Pick-Up And Drop Off The Rental Equipment?',
  answer: 'We deliver and pick up your equipment at no extra charge to you. Simply provide us with a delivery and pickup location, and we will manage the rest. For equipment delivered to hotels, simply leave it at the front desk and we will pick it up.'
}, {
  question: 'Can I Fit My Wheelchair Or Scooter In The Trunk Of My Car?',
  answer: "Yes. All transportable scooters may be readily disassembled to fit in your car's trunk. All of the manual wheelchairs we hire are collapsible and can be stored in the trunk of your car. Heavier power chairs may be transported in an SUV or Minivan."
}, {
  question: 'As A Travel Agent Can I Reserve Equipment For My Clients?',
  answer: 'Yes. Please take advantage of the benefits offered to travel agents for booking rentals for clients. Call our toll-free number at 1-800-395-4459 or 1-202-367-9965 to participate in this program.'
}, {
  question: 'What Advice Can You Give On Recharging The Batteries?',
  answer: 'Your battery must be charged on a regular basis. Simply insert the power cable into an outlet at the end of the day and let it charge overnight.'
}];
export function FAQPage({
  onNavigate
}: FAQPageProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="flex-grow w-full bg-brand-gray" data-id="element-1041">
      <PageHeader title="Questions People Ask" breadcrumb="FAQs" onNavigateHome={() => onNavigate('home')} data-id="element-1042" />

      <section className="py-16 md:py-24" data-id="element-1043">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1044">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="space-y-4" data-id="element-1045">
            {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.04
            }} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-1046">
                  <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full flex items-center justify-between p-6 text-left cursor-pointer group" data-id="element-1047">
                    <h3 className={`font-bold text-base md:text-lg pr-4 transition-colors ${isOpen ? 'text-brand-red' : 'text-brand-navy group-hover:text-brand-red'}`} data-id="element-1048">
                      {faq.question}
                    </h3>
                    <div className={`w-8 h-8 rounded-full shrink-0 flex items-center justify-center transition-all ${isOpen ? 'bg-brand-red text-white rotate-180' : 'bg-brand-gray text-brand-navy'}`} data-id="element-1049">
                      <ChevronDownIcon className="w-4 h-4" data-id="element-1050" />
                    </div>
                  </button>
                  <AnimatePresence initial={false} data-id="element-1051">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25,
                  ease: [0.22, 1, 0.36, 1]
                }} className="overflow-hidden" data-id="element-1052">
                        <div className="px-6 pb-6 pt-0" data-id="element-1053">
                          <div className="border-t border-gray-100 pt-4" data-id="element-1054">
                            <p className="text-brand-charcoal/70 leading-relaxed" data-id="element-1055">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </motion.div>

          {/* Contact CTA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="mt-16 bg-brand-navy rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-1056">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-brand-red/10 rounded-full blur-3xl pointer-events-none" data-id="element-1057"></div>
            <div className="relative z-10" data-id="element-1058">
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-1059">
                Still have questions?
              </h3>
              <p className="text-white/60 mb-8 max-w-lg mx-auto" data-id="element-1060">
                Our friendly team is here to help. Give us a call or send us a
                message and we'll get back to you as soon as possible.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1061">
                <a href="tel:+18003954459" className="inline-flex items-center gap-2 px-8 py-3 bg-brand-red text-white rounded-xl font-bold hover:bg-red-700 transition-colors shadow-md" data-id="element-1062">
                  <PhoneIcon className="w-4 h-4" data-id="element-1063" />1 (800) 395-4459
                </a>
                <button onClick={() => onNavigate('contact')} className="inline-flex items-center gap-2 px-8 py-3 bg-white/10 text-white border border-white/20 rounded-xl font-bold hover:bg-white/20 transition-colors" data-id="element-1064">
                  Contact Us
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>;
}